import { Component, EventEmitter, OnInit, OnDestroy, Output } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CookieService } from '../../../../core/services/cookie-service/cookie.service';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/core/services/auth-service/auth.service';
import { NotificationService } from 'src/app/core/services/notification-service/notification.service';
import { finalize, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  link?: string;
  submenu?: MenuItem[];
  isHeader?: boolean;
  disabled?: boolean;
  hide?: boolean;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Output() sidebarCollapsed = new EventEmitter<boolean>();

  activeDropdown: string[] = [];
  appVersion: string = environment.version;
  isCollapsed: boolean = false;
  isLoading: boolean = false;
  isMobileOpen: boolean = false;

  isDesktop: boolean = window.innerWidth >= 1024;
  private destroy$ = new Subject<void>();

  menuItems: MenuItem[] = [
    {
      id: 'dashboard',
      label: 'Accueil',
      icon: 'dashboard',
      link: '/dashboard'
    },

    // ------ Compte et Adhésion -----------------------
    {
      id: 'header-compte-adhesion',
      label: 'Compte et Adhésion',
      isHeader: true
    },
    {
      id: 'mon-compte',
      label: 'Mon compte',
      icon: 'mc',
      link: '/mon-compte/liste'
    },
    {
      id: 'verfication-adherent',
      label: 'Vérification adhérent',
      icon: 'recla',
      link: '/verfication-adherent/liste',
      hide: true
    },

    // ------ Prestations -----------------------
    {
      id: 'header-prestations',
      label: 'Prestations',
      isHeader: true
    },
    {
      id: 'pecs',
      label: 'Demandes et suivi de mes prises en charge',
      icon: 'pec',
      link: '/pecs/liste'
    },
    {
      id: 'reglement',
      label: 'Règlement',
      icon: 'bordereau_paiement',
      link: '/reglement/liste'
    },

    // ------ Assistance -----------------------
    {
      id: 'header-assistance',
      label: 'Assistance',
      isHeader: true
    },
    {
      id: 'reclamations',
      label: 'Réclamations',
      icon: 'recla',
      link: '/reclamations/liste'
    }
  ];

  constructor(
    public storeData: Store<any>,
    public router: Router,
    private cookieService: CookieService,
    private authService: AuthService,
    private notificationService: NotificationService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.activeDropdown = [];
        this.setActiveDropdown();
        if (window.innerWidth < 1024) {
          this.storeData.dispatch({ type: 'closeSidebar' });
        }
      }
    });
  }

  ngOnInit(): void {
    this.setActiveDropdown();

    if (window.innerWidth < 1024) {
      this.isCollapsed = false;
      this.sidebarCollapsed.emit(false);
    }

    // Show "Vérification adhérent" only if showBtn is true
    const showBtn = this.cookieService.getData('showBtn');
    if (showBtn === true || showBtn === 'true') {
      const item = this.menuItems.find(m => m.id === 'verfication-adherent');
      if (item) item.hide = false;
    }
    // Sync isMobileOpen with store.sidebar
    this.storeData.select((d: any) => d.index)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: any) => {
        this.isMobileOpen = state?.sidebar === true;
      });
    // Listen to window resize to update desktop state
    window.addEventListener('resize', this.onResize);
  }

  onResize = (): void => {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 1024;
    if (!this.isDesktop && this.isCollapsed) {
      this.isCollapsed = false;
      this.sidebarCollapsed.emit(false);
    }
    // Passer en desktop : fermer la sidebar mobile si elle était ouverte
    if (!wasDesktop && this.isDesktop) {
      this.storeData.dispatch({ type: 'closeSidebar' });
    }
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.onResize);
  }

  get sidebarStyle(): { [key: string]: string } {
    if (this.isDesktop) {
      return {
        'top': '88px',
        'height': 'calc(100vh - 88px)',
        'transform': 'translateX(0)'
      };
    }
    return {
      'top': '88px',
      'height': 'calc(100vh - 88px)',
      'transform': this.isMobileOpen ? 'translateX(0)' : 'translateX(-100%)'
    };
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarCollapsed.emit(this.isCollapsed);
  }

  toggleMobileMenu(): void {
    this.activeDropdown = [];
    if (!this.isDesktop) {
      this.storeData.dispatch({ type: 'closeSidebar' });
    }
  }

  isRouteActive(route: string | undefined): boolean {
    if (!route) return false;
    const moduleFromRoute = route.split('/')[1];
    const moduleFromUrl = this.router.url.split('/')[1];
    return moduleFromRoute === moduleFromUrl;
  }

  hasActiveSubmenuItem(item: MenuItem): boolean {
    if (!item.submenu) return false;
    const currentModule = this.router.url.split('/')[1];
    return item.submenu.some(subItem => {
      if (!subItem.link) return false;
      return currentModule === subItem.link.split('/')[1];
    });
  }

  onSubmenuItemClick(_menuId: string): void {
    this.toggleMobileMenu();
  }

  setActiveDropdown(): void {
    const pathname = window.location.pathname;
    for (const item of this.menuItems) {
      if (item.submenu) {
        for (const subItem of item.submenu) {
          if (subItem.link && pathname.includes(subItem.link)) {
            if (!this.activeDropdown.includes(item.id)) {
              this.activeDropdown.push(item.id);
            }
            break;
          }
        }
      }
    }
  }

  toggleAccordion(name: string): void {
    if (this.activeDropdown.includes(name)) {
      this.activeDropdown = this.activeDropdown.filter(d => d !== name);
    } else {
      this.activeDropdown = [];
      this.activeDropdown.push(name);
    }
  }

  logout(): void {
    this.storeData.dispatch({ type: 'toggleMainLoader', payload: true });

    this.authService.LogOutUser()
      .pipe(
        finalize(() => {
          this.storeData.dispatch({ type: 'toggleMainLoader', payload: false });
        }),
        catchError((_error) => {
          this.notificationService.error('Erreur', 'Erreur lors de la déconnexion');
          return of(null);
        })
      )
      .subscribe({
        next: () => {
          this.cookieService.removeCookies();
          this.router.navigate(['/login']);
        }
      });
  }
}
